<template>
  <v-dialog v-model="dialog" max-width="600px" persistent>
    <v-card>
      <v-card-title class="headline" primary-title>
        Create Certificate
      </v-card-title>

      <v-card-text>
        <v-container grid-list-md>
          <v-layout wrap>
            <v-flex xs12>
              <v-text-field
                v-model="form.certificate_id"
                label="Certificate ID"
                placeholder="e.g., prod-cert-001"
                outlined
                required
              ></v-text-field>
            </v-flex>

            <v-flex xs12>
              <v-select
                v-model="form.device_id"
                :items="values.devices"
                label="Device ID"
                outlined
                required
              ></v-select>
            </v-flex>

            <v-flex xs12>
              <v-select
                v-model="form.cert_type"
                :items="certTypeOptions"
                label="Certificate Type"
                outlined
                required
              ></v-select>
            </v-flex>

            <v-flex xs12>
              <v-select
                v-model="form.key_algorithm"
                :items="keyAlgorithmOptions"
                label="Key Algorithm"
                outlined
                required
              ></v-select>
            </v-flex>

            <v-flex xs12 v-if="form.cert_type === 'self-signed'">
              <v-text-field
                v-model.number="form.days_valid"
                label="Valid for (days)"
                type="number"
                placeholder="365"
                outlined
                min="1"
                max="3650"
              ></v-text-field>
            </v-flex>

            <v-flex xs12>
              <v-text-field
                v-model="form.device_profile"
                label="Device Profile (Optional)"
                placeholder="e.g., iot-standard"
                outlined
              ></v-text-field>
            </v-flex>

            <v-flex xs12>
              <v-alert type="info" outlined>
                <div class="body-2">
                  <strong>Certificate Types:</strong>
                </div>
                <ul style="margin: 0.5rem 0; padding-left: 1.5rem;">
                  <li>
                    <strong>Self-Signed:</strong> For testing, signed by itself
                  </li>
                  <li>
                    <strong>CSR:</strong> Certificate Signing Request for
                    external CAs
                  </li>
                  <li>
                    <strong>AWS-Managed:</strong> Created and managed by AWS IoT
                    Core
                  </li>
                </ul>
              </v-alert>
            </v-flex>
          </v-layout>
        </v-container>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="grey darken-1" text @click="abort">
          Cancel
        </v-btn>
        <v-btn
          color="purple"
          text
          @click="createCertificate"
          :loading="isCreating"
          :disabled="!isFormValid"
        >
          Create
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  props: {
    values: {
      type: Object,
      default: () => ({
        devices: [],
      }),
    },
  },

  data() {
    return {
      dialog: true,
      isCreating: false,
      form: {
        certificate_id: "",
        device_id: "",
        cert_type: "self-signed",
        key_algorithm: "ECDSA",
        days_valid: 365,
        device_profile: "",
      },
      certTypeOptions: [
        {text: "Self-Signed (for testing)", value: "self-signed"},
        {text: "CSR (Certificate Signing Request)", value: "csr"},
        {text: "AWS-Managed (AWS IoT Core)", value: "aws-managed"},
      ],
      keyAlgorithmOptions: [
        {text: "ECDSA (Prime256v1, 256-bit)", value: "ECDSA"},
        {text: "RSA (2048-bit)", value: "RSA"},
      ],
    };
  },

  computed: {
    isFormValid() {
      return (
        this.form.certificate_id.trim().length > 0 &&
        this.form.device_id &&
        this.form.cert_type &&
        this.form.key_algorithm &&
        (this.form.cert_type === "self-signed"
          ? this.form.days_valid > 0
          : true)
      );
    },
  },

  methods: {
    async createCertificate() {
      if (!this.isFormValid) {
        return;
      }

      this.isCreating = true;
      try {
        const payload = {
          certificate_id: this.form.certificate_id,
          device_id: this.form.device_id,
          cert_type: this.form.cert_type,
          key_algorithm: this.form.key_algorithm,
          days_valid:
            this.form.cert_type === "self-signed" ? this.form.days_valid : 365,
          device_profile: this.form.device_profile || null,
        };

        // Dispatch Vuex action to create certificate
        await this.$store.dispatch("certificates/createCertificate", payload);
        this.$emit("completed");
      } catch (error) {
        console.error("Error creating certificate:", error);
        this.$store.commit(
          "setLastOperationMessage",
          `Error creating certificate: ${error.message}`,
        );
        this.$store.commit("setLastOperationSuccess", false);
      } finally {
        this.isCreating = false;
      }
    },

    abort() {
      this.$emit("abort");
    },
  },
};
</script>

<style scoped>
.body-2 {
  font-size: 0.875rem;
}

ul {
  margin: 0.5rem 0;
  padding-left: 1.5rem;
}

li {
  margin: 0.25rem 0;
}
</style>
